import { useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Painel, useCarga } from '../comum';

/**
 * Vulnerabilidades do parque, priorizadas pelo que **vai** acontecer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 **A tela inteira existe por causa de uma ordenação.**
 *
 * O KACE lista patches por identificador e pela criticidade que o fabricante
 * declarou. Ele não sabe se alguém está explorando aquilo hoje — e essa é a
 * única informação que muda o que você faz na segunda de manhã.
 *
 * Aqui a ordem é: **usado em ransomware → exploração conhecida → EPSS → CVSS**.
 * Um 7.0 explorado ativamente vem acima de um 9.8 que ninguém sabe explorar,
 * porque a nota mede o estrago possível e o EPSS mede a chance de acontecer.
 * Ordenar por nota coloca o item que vai te derrubar na quinta página.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface Vulnerabilidade {
  readonly cve: string;
  readonly url: string | null;
  readonly cvss: number | null;
  readonly epssPercent: number | null;
  readonly exploracaoConhecida: boolean;
  readonly usadoEmRansomware: boolean;
  readonly maquinas: number;
}

interface RespostaCve {
  readonly total: number;
  readonly resumo: {
    readonly ransomware: number;
    readonly exploracaoConhecida: number;
    readonly maquinasAfetadas: number;
  };
  readonly itens: readonly Vulnerabilidade[];
  readonly fonte: string;
}

interface PatchFaltando {
  readonly identificador: string;
  readonly titulo: string;
  readonly criticidade: string | null;
  readonly maquinas: number;
}

interface RespostaPatches {
  readonly total: number;
  readonly itens: readonly PatchFaltando[];
  readonly fonte: string;
}

export function Vulnerabilidades({
  aoPerderSessao,
}: {
  aoPerderSessao: () => void;
}): ReactNode {
  const [aba, setAba] = useState<'cve' | 'patches'>('cve');

  return (
    <div className="tela">
      <div className="cabecalho-tela">
        <h1>Vulnerabilidades</h1>
        <p className="suave">
          O que o appliance coletou e não consegue priorizar. Vem do banco do KACE —
          a API REST não expõe CVE nenhum.
        </p>
      </div>

      <div className="abas">
        <button className={aba === 'cve' ? 'ativa' : ''} onClick={() => setAba('cve')}>
          Por vulnerabilidade
        </button>
        <button className={aba === 'patches' ? 'ativa' : ''} onClick={() => setAba('patches')}>
          Por patch
        </button>
      </div>

      {aba === 'cve' ? (
        <ListaCve aoPerderSessao={aoPerderSessao} />
      ) : (
        <ListaPatches aoPerderSessao={aoPerderSessao} />
      )}
    </div>
  );
}

/**
 * A recusa quando o banco está desligado.
 *
 * ⚠️ Ela **explica o que ganha ao ligar**, em vez de só dizer que está
 * desligado. Uma tela vazia com "recurso indisponível" é indistinguível de
 * funcionalidade quebrada — e o técnico não vai adivinhar que existe um
 * interruptor em outra tela.
 */
function BancoDesligado({ motivo }: { motivo: string }): ReactNode {
  return (
    <Painel titulo="Leitura do banco do KACE desligada">
      <Aviso tipo="info">{motivo}</Aviso>
      <p className="suave">
        Com ela ligada, esta tela mostra <strong>quais CVEs existem no seu parque</strong>,
        quais estão sendo explorados hoje e em quantas máquinas — informação que o
        KACE coleta e não exibe em lugar nenhum.
      </p>
    </Painel>
  );
}

function ListaCve({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const carga = useCarga<RespostaCve>(
    async () => api.get('/api/vulnerabilidades'),
    [],
    aoPerderSessao,
  );

  if (carga.carregando) return <p className="suave">Consultando o banco do KACE…</p>;
  if (carga.erro !== null) return <BancoDesligado motivo={carga.erro} />;
  if (carga.dados === null) return null;

  const { resumo, itens, fonte } = carga.dados;

  return (
    <>
      <div className="cartoes-resumo">
        <ResumoNumero
          valor={resumo.ransomware}
          rotulo="usados em ransomware"
          grave={resumo.ransomware > 0}
        />
        <ResumoNumero
          valor={resumo.exploracaoConhecida}
          rotulo="com exploração conhecida"
          grave={resumo.exploracaoConhecida > 0}
        />
        <ResumoNumero valor={itens.length} rotulo="vulnerabilidades no parque" grave={false} />
      </div>

      <Painel titulo="Por urgência real" sub={fonte}>
        {itens.length === 0 ? (
          <p className="suave">
            Nenhuma vulnerabilidade pendente encontrada. Isto vale para os patches que o
            KACE classificou como aplicáveis e não instalados.
          </p>
        ) : (
          <div className="tabela-rolavel">
            <table>
              <thead>
                <tr>
                  <th>CVE</th>
                  <th>Sinais</th>
                  <th>CVSS</th>
                  <th>EPSS</th>
                  <th>Máquinas</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((v) => (
                  <tr key={v.cve} className={v.usadoEmRansomware ? 'linha-critica' : ''}>
                    <td>
                      {v.url === null ? (
                        v.cve
                      ) : (
                        <a href={v.url} target="_blank" rel="noreferrer">
                          {v.cve}
                        </a>
                      )}
                    </td>
                    <td>
                      {v.usadoEmRansomware && <span className="etiqueta grave">ransomware</span>}
                      {v.exploracaoConhecida && (
                        <span className="etiqueta alerta">explorado</span>
                      )}
                      {!v.usadoEmRansomware && !v.exploracaoConhecida && (
                        <span className="suave">—</span>
                      )}
                    </td>
                    {/* Nota ausente é "o NVD ainda não pontuou", não zero. */}
                    <td>{v.cvss === null ? <span className="suave">—</span> : v.cvss.toFixed(1)}</td>
                    <td>
                      {v.epssPercent === null ? (
                        <span className="suave">—</span>
                      ) : (
                        `${v.epssPercent.toFixed(1)}%`
                      )}
                    </td>
                    <td>{v.maquinas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Painel>
    </>
  );
}

function ListaPatches({ aoPerderSessao }: { aoPerderSessao: () => void }): ReactNode {
  const carga = useCarga<RespostaPatches>(
    async () => api.get('/api/patches-faltando'),
    [],
    aoPerderSessao,
  );

  if (carga.carregando) return <p className="suave">Consultando o banco do KACE…</p>;
  if (carga.erro !== null) return <BancoDesligado motivo={carga.erro} />;
  if (carga.dados === null) return null;

  return (
    <Painel
      titulo="Quais patches faltam"
      sub={carga.dados.fonte}
    >
      <p className="suave">
        Aqui o <strong>patch</strong> é o sujeito da frase, não a máquina. É a diferença
        entre saber que existe um problema e saber o que instalar.
      </p>
      <div className="tabela-rolavel">
        <table>
          <thead>
            <tr>
              <th>Patch</th>
              <th>Título</th>
              <th>Criticidade</th>
              <th>Máquinas</th>
            </tr>
          </thead>
          <tbody>
            {carga.dados.itens.map((p) => (
              <tr key={p.identificador}>
                <td>{p.identificador}</td>
                <td>{p.titulo}</td>
                <td>{p.criticidade ?? <span className="suave">—</span>}</td>
                <td>{p.maquinas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Painel>
  );
}

function ResumoNumero({
  valor,
  rotulo,
  grave,
}: {
  valor: number;
  rotulo: string;
  grave: boolean;
}): ReactNode {
  return (
    <div className={`cartao-resumo${grave ? ' critico' : ''}`}>
      <span className="numero">{valor}</span>
      <span className="rotulo">{rotulo}</span>
    </div>
  );
}
